import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeDTO } from "utils/helpers/models/host-dtos/badge.dto";
import useHost from "../useHooks";
import { useSelector } from "react-redux";
import Breadcrumb, { BreadcrumbLink } from "components/breadcrumbs";
import { siteRoutes } from "utils/helpers/enums/routes";
import CreateBadgeModal from "./component";
import { SUBSCRIPTION_TYPES } from "utils/helpers/enums/shared.enum";
import bgCards from "assets/images/others/host-side-images/bgCards.png";
import useStore from "hooks/useStore";
import CustomMessageDisplay from "components/data-not-found/CustomMessageDisplay";
import noFoundGif from "assets/images/gifs/noFoundGif.gif";

const EventBadges = () => {
  const navigate = useNavigate();
  const { isProfileCompleted } = useStore();
  const [showCreateForm, setShowCreateForm] = useState(false);
  const { isLoading } = useSelector((state: any) => state.sharedReducer);
  const [data, setData] = useState<BadgeDTO[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true);
  const { getBadges, getAdminBadges, createBadge } = useHost();

  const registeredData = useSelector(
    (state: any) => state.sharedReducer.registeredData
  );

  // Check profile completion on mount - redirect to home if not completed
  useEffect(() => {
    const profileCompleted = isProfileCompleted();
    if (!profileCompleted) {
      navigate(siteRoutes.home, { replace: true });
    }
  }, [isProfileCompleted, navigate]);


  // Determine initial active tab based on subscription type
  const getInitialActiveTab = () => {
    const subscriptionType = registeredData?.user?.subscription?.product?.subscriptionType;
    // Show "myBadges" only for TIER_1 and TIER_2, default to "adminBadges" for BASIC
    return subscriptionType === SUBSCRIPTION_TYPES.BASIC ? "adminBadges" : "myBadges";
  };

  const [activeTab, setActiveTab] = useState(getInitialActiveTab);

  const breadcrumbLinks: BreadcrumbLink[] = [
    { title: "Home", path: siteRoutes.home },
    { title: "Event Badges", path: siteRoutes.hostEventBadges },
  ];

  // Update active tab when registeredData changes
  useEffect(() => {
    const newActiveTab = getInitialActiveTab();
    if (newActiveTab !== activeTab) {
      setActiveTab(newActiveTab);
    }
  }, [registeredData]);

  useEffect(() => {
    // Initialize with the correct data based on active tab
    if (activeTab === "adminBadges") {
      getAdminBadges(setData);
    } else {
      getBadges(setData);
    }
  }, [activeTab]);

  // Mark initial load as complete when loading finishes
  useEffect(() => {
    if (isInitialLoad && !isLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, isInitialLoad]);

  const handleChangeTab = (tab: string) => {
    // Clear data first to prevent showing old images
    setData([]);
    setActiveTab(tab);
    
    // Then load new data
    if (tab === "adminBadges") {
      getAdminBadges(setData);
    } else {
      getBadges(setData);
    }
  };

  const handleBadgeCreated = () => {
    if (activeTab === "myBadges") {
      getBadges(setData);
    } else {
      getAdminBadges(setData);
    }
  };

  return (
    <div className="container min-h-screen">
      <Breadcrumb links={breadcrumbLinks} />

      <div className="">
        <div className="flex justify-between items-center my-4">
          <h2 className="text-2xl font-bold  text-blackie">Event Badges</h2>
          {activeTab === "myBadges" && 
           registeredData?.user?.subscription?.product?.subscriptionType !== SUBSCRIPTION_TYPES.BASIC && (
            <div className="flex justify-center">
              <button
                className="flex justify-center px-4 py-2 rounded-full font-semibold bg-fade text-primaryBlueText transition-colors duration-200"
                onClick={() => setShowCreateForm(true)}
              >
                + Add New Badge
              </button>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8">
          {/* Show My Badges tab only for TIER_1 and TIER_2 users */}
          {registeredData?.user?.subscription?.product?.subscriptionType !== SUBSCRIPTION_TYPES.BASIC && (
            <button
              className={`py-2 px-4 transition duration-300 ${
                activeTab === "myBadges"
                  ? "text-lightBlueColor border-b-2 border-lightBlueColor"
                  : "text-grayText hover:text-lightBlueColor"
              }`}
              onClick={() => handleChangeTab("myBadges")}
            >
              My Badges
            </button>
          )}

          <button
            className={`py-2 px-4 transition duration-300 ${
              activeTab === "adminBadges"
                ? "text-lightBlueColor border-b-2 border-lightBlueColor"
                : "text-grayText hover:text-lightBlueColor"
            }`}
            onClick={() => handleChangeTab("adminBadges")}
          >
            Admin Badges
          </button>
        </div>

        <div className="flex flex-wrap gap-4 relative">
          {data.length > 0
            ? data.map((badge: any, index) => (
                <div
                  key={index}
                  style={{
                    backgroundImage: `url(${bgCards})`,
                  }}
                  className="relative flex flex-col items-center justify-center p-4 rounded-lg w-[235px] border border-[#D9D7D7] min-h-[200px] cursor-pointer"
                >
                  <img
                    key={`${activeTab}-${badge.id}-${index}`}
                    className="w-[98px] min-h-[108px] object-cover transition-transform duration-200 hover:scale-105"
                    src={badge.image.name}
                    alt={badge.name}
                  />

                  <span className="text-lg font-semibold mt-3 text-black">
                    {badge?.name}
                  </span>
                </div>
              ))
            : null}

          <CustomMessageDisplay
            show={!isLoading && !data.length && !isInitialLoad}
            title={
              activeTab === "myBadges"
                ? "No Badges Available"
                : "No Admin Badges Available"
            }
            slogan={
              activeTab === "myBadges"
                ? "You haven't created any badges yet. Click 'Add New Badge' to create your first badge!"
                : "No admin badges are available at the moment. Admin badges will appear here when they're added."
            }
            image={noFoundGif}
            imageAlt="No badges found"
            className="py-12"
            imageClassName="w-24 h-24"
            titleClassName="text-2xl font-bold text-blackie"
            sloganClassName="text-gray-500 text-base"
          />
        </div>
        {showCreateForm && (
          <CreateBadgeModal
            isOpen={showCreateForm}
            onClose={() => setShowCreateForm(false)}
            onBadgeCreated={handleBadgeCreated}
            createBadge={createBadge}
          />
        )}
      </div>
    </div>
  );
};

export default EventBadges;
